import React, { useEffect, useState } from "react";
import "../../../styles/Profiles.css";
import { API_BASE_URL } from "../../../config";
import ChangePassword from "../../User/ChangePassword";
import NotificationPreferences from "../../Shared/NotificationPreferences";

// This form used to edit an address block and, worse, payout_method and
// payout_details -- a free-text field the placeholder invited you to type a
// bank account number into, stored unencrypted and read by nothing. Payouts
// run through Stripe Connect; Stripe collects and holds the bank details.
// Withdrawals live on the Payout & balance tab.

export default function VendorProfile() {
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({ business_name: "", display_name: "", email: "", public_contact: "" });
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const token = localStorage.getItem("token");
    const auth = { Authorization: `Bearer ${token}` };

    function load(data) {
        setProfile(data);
        setForm({
            business_name: data.business_name || "",
            display_name: data.display_name || "",
            email: data.email || "",
            public_contact: data.public_contact || "",
        });
    }

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/vendor/profile`, { headers: auth })
            .then(async (res) => {
                if (!res.ok) throw new Error();
                load(await res.json());
            })
            .catch(() => setError("Failed to load your profile."))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleSave(e) {
        e.preventDefault();
        setSaving(true);
        setError("");
        setNotice("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/vendor/profile`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", ...auth },
                body: JSON.stringify(form),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.message || "Could not save those changes.");
                setSaving(false);
                return;
            }
            load(data);
            setEditing(false);
            setNotice(
                data.email_verified
                    ? "Saved."
                    : "Saved. Confirm your new email address using the link we just sent."
            );
        } catch {
            setError("Network error. Nothing was saved.");
        }
        setSaving(false);
    }

    async function resendVerification() {
        setNotice("");
        setError("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
                method: "POST", headers: auth,
            });
            const data = await res.json().catch(() => ({}));
            setNotice(data.message || "Confirmation email sent.");
        } catch {
            setError("Could not send the confirmation email.");
        }
    }

    if (loading) return <div className="wb-page">Loading your profile…</div>;
    if (!profile) return <div className="wb-page wb-note wb-note--danger">{error}</div>;

    const field = (label, name, type = "text", hint = null) => (
        <label className="wb-field">
            <span className="wb-label">{label}</span>
            <input
                className="wb-input" type={type} value={form[name]}
                onChange={(e) => setForm({ ...form, [name]: e.target.value })}
            />
            {hint && <span className="wb-hint">{hint}</span>}
        </label>
    );

    return (
        <div className="wb-page wb-page--narrow">
            <div className="wb-page-head">
                <h1>Your account</h1>
            </div>

            {notice && <div className="wb-note wb-note--ok">{notice}</div>}
            {error && <div className="wb-note wb-note--danger">{error}</div>}

            <section className="wb-card" style={{ marginTop: "1rem" }}>
                {editing ? (
                    <form onSubmit={handleSave}>
                        {field("Business name", "business_name", "text",
                               "Shown to advertisers browsing your screens.")}
                        {field("Your name", "display_name")}
                        {field("Email", "email", "email",
                               "You sign in with this. Changing it needs confirming again.")}
                        {field("Contact for advertisers", "public_contact", "text",
                               "Optional. A phone number or shop email.")}
                        <div className="wb-row">
                            <button type="submit" className="wb-btn wb-btn--primary" disabled={saving}>
                                {saving ? "Saving…" : "Save"}
                            </button>
                            <button
                                type="button" className="wb-btn wb-btn--secondary"
                                onClick={() => { setEditing(false); load(profile); }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                ) : (
                    <>
                        <div className="wb-field">
                            <span className="wb-label">Business name</span>
                            <div>{profile.business_name}</div>
                        </div>
                        <div className="wb-field">
                            <span className="wb-label">Your name</span>
                            <div>{profile.display_name}</div>
                        </div>
                        <div className="wb-field">
                            <span className="wb-label">Email</span>
                            <div className="wb-row">
                                <span>{profile.email}</span>
                                {profile.email_verified ? (
                                    <span className="wb-badge wb-badge--ok">Confirmed</span>
                                ) : (
                                    <span className="wb-badge wb-badge--warn">Unconfirmed</span>
                                )}
                            </div>
                            {!profile.email_verified && (
                                <button
                                    type="button" className="wb-btn wb-btn--ghost wb-btn--sm"
                                    onClick={resendVerification} style={{ marginTop: 4, paddingLeft: 0 }}
                                >
                                    Send the confirmation email again
                                </button>
                            )}
                        </div>
                        <div className="wb-field">
                            <span className="wb-label">Contact for advertisers</span>
                            <div>{profile.public_contact || "—"}</div>
                        </div>
                        <div className="wb-field">
                            <span className="wb-label">Payouts</span>
                            <div>
                                {profile.payouts_connected ? (
                                    <span className="wb-badge wb-badge--ok">Stripe connected</span>
                                ) : (
                                    <span className="wb-badge wb-badge--warn">Not set up</span>
                                )}
                            </div>
                            <span className="wb-hint">
                                Bank details are held by Stripe. Set up or change them on the
                                Payout &amp; balance tab.
                            </span>
                        </div>
                        <button className="wb-btn wb-btn--secondary" onClick={() => setEditing(true)}>
                            Edit
                        </button>
                    </>
                )}
            </section>

            <section style={{ marginTop: "2rem" }}>
                <h2>Password</h2>
                <div className="wb-card">
                    <ChangePassword />
                </div>
            </section>

            <section style={{ marginTop: "2rem" }}>
                <h2>Notifications</h2>
                <div className="wb-card">
                    <NotificationPreferences />
                </div>
            </section>
        </div>
    );
}
